<template>
    <div class="negative-button-wrapper">
        <button
        class="button-negative"
        :disabled="disabled"
        :class="[
            buttonStyle,
            buttonSize,
            buttonVariant,
            { 'icon-only': !label }
        ]"
        @click.stop="emit('clicked')">
            <!-- Button Icon -->
            <app-icon class="icon" :class="{ 'icon-right': iconPosition === 'right' }"/>
            <!-- Button label -->
            <div v-if="label && buttonStyle !== 'round'" class="label">{{ label }}</div>
        </button>
        <!-- External Label -->
        <div v-if="buttonStyle === 'round'" class="external-label" :class="{ 'disabled': disabled }">
            {{ label }}
        </div>
    </div>
</template>

<script setup>
/* Vue */
import { computed, defineAsyncComponent } from 'vue'
import { iconModules } from '@/assets/icons/iconModules'
/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props received:
@label {String}
@icon {String}
@iconPosition {String} can be [right (default), left]
@buttonStyle {String} can be [square (default), round]
@buttonSize {String} can be [medium (default), small]
@buttonVariant {String} can be [full (default), only-border]
@disabled {Boolean}
*/
const props = defineProps({
    label: String,
    icon: {
        type: String,
        default: 'close'
    },
    iconPosition: {
        type: String,
        default: 'left'
    },
    buttonStyle: {
        type: String,
        default: 'square'
    },
    buttonSize: {
        type: String,
        default: 'medium'
    },
    buttonVariant: {
        type: String,
        default: 'full'
    },
    disabled: Boolean
})

/* Computed */
const appIcon = computed(() => {
    const icon = props.icon
	return defineAsyncComponent(iconModules[`/src/assets/icons/${icon}.svg`])
})
</script>

<style lang="scss" scoped>
.negative-button-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .button-negative {
        width: max-content;

        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        @include H7-Medium;
        color: var(--secondary-light-4);

        cursor: pointer;

        svg.icon {
            flex-shrink: 0;

            width: 16px;
            height: 16px;

            &.icon-right {
                order: 2;
            }
        }

        /* Style classes */
        &.square {
            height: 48px;

            padding: 18px 16px;
            border-radius: 8px;

            &.full {
                background-color: var(--negative);

                svg.icon {
                    :deep(.change-color-fill) {
                        fill: var(--secondary-light-4);
                    }
                }

                @media (hover: hover) and (pointer: fine) {
                    &:hover {
                        background-color: var(--button-negative-state-hover);
                    }
                }

                &:active {
                    background-color: var(--button-negative-state-press);
                }
            }

            &:not(.only-border) {
                box-shadow: var(--box-shadow-3);
            }

            &.only-border {
                color: var(--negative);
                border: 1px solid var(--negative);

                svg.icon {
                    :deep(.change-color-fill) {
                        fill: var(--negative);
                    }
                }

                @media (hover: hover) and (pointer: fine) {
                    &:hover {
                        background-color: var(--secondary-light-3);
                    }
                }

                &:active {
                    background-color: var(--secondary-light-1);
                }
            }
        }

        &.round {
            width: 40px;
            height: 40px;

            border: 1px solid var(--negative);
            border-radius: 50%;

            svg.icon {
                :deep(.change-color-fill) {
                    fill: var(--negative);
                }
            }

            @media (hover: hover) and (pointer: fine) {
                &:hover {
                    background-color: var(--secondary-light-3);
                }
            }

            &:active {
                background-color: var(--secondary-light-1);
            }
        }

        &:disabled {
            cursor: default;
            pointer-events: none;
            opacity: 0.16;
        }

        &.small {
            height: 40px;

            &.icon-only {
                width: 40px
            }
        }
    }

    .external-label {
        @include H7-Medium;
        line-height: 16px;

        color: var(--negative);
        cursor: default;

        &.disabled {
            opacity: 0.16;
        }
    }
}
</style>
